<template>
  <aha-modal @hide="hideModal()" :title="title">
    <p>{{ content }}</p>
    <template #action>
      <aha-button
        variant="danger"
        @click="handleDeleteSlides()"
        has-margin-right
      >
        OK
      </aha-button>
      <aha-link @click="hideModal()">
        <span class="font-bold">Cancel</span>
      </aha-link>
    </template>
  </aha-modal>
</template>
<script>
import { mapState, mapActions } from 'vuex'

// api
import SlideApi from '@/api/slide'

// utils
import utils from '@/util'
import PresentationUtils from '@/utils/presentation'
import gaCategories from '@/constant/gaCategories'
import EventBus from '../../eventBus'
import { removeObjectFromIndexedDB, Stores } from '@/utils/indexedDB/indexed.db'

export default {
  props: {
    slides: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    content: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      deletedSlides: [],
    }
  },
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      slideActive: (state) => state.slideActive,
    }),
  },
  methods: {
    ...mapActions([
      'updateSlideActiveAction',
      'setSyncLoadingAction',
    ]),

    handleDeleteSlides() {
      if (!this.slides || this.slides.length === 0) { return }
      utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Modal delete slide > Confirm delete slide', null, null, {
        slideId: this.slides.map((i) => i.id).join(','),
      })
      this.slides.forEach((s) => {
        s.index = this.presentation.slides.findIndex(
          (pslide) => s.id === pslide.id,
        )

        this.deletedSlides.push(s)
      })

      this.slides.forEach((s) => {
        this.addQuizAndLeaderboardToSlidesToDelete(s)
      })
      this.handleCallAPIDeleteSlides()
    },

    removeImageCaches(slide) {
      if (!slide.id) return
      removeObjectFromIndexedDB(slide.id, Stores.backgroundImageStore)
      removeObjectFromIndexedDB(slide.id, Stores.questionImageStore)
      if (slide.SlideOptions && slide.SlideOptions.length > 0) {
        slide.SlideOptions.forEach((slideOption) => {
          removeObjectFromIndexedDB(slideOption.id, Stores.slideOptionImageStore)
        })
      }
    },

    addQuizAndLeaderboardToSlidesToDelete(currentSlide) {
      const sortedSlides = this.presentation.slides.sort((a, b) => a.order - b.order)
      const quizSlides = this.presentation.slides.filter((slide) => slide.type === 'pickAnswer')
      const deletedQuizSlides = this.deletedSlides.filter((slide) => slide.type === 'pickAnswer')
      let nextSlide = null
      let isNextSlideLastSlide = false

      for (let i = 0; i < sortedSlides.length - 1; i++) {
        if (sortedSlides[i].id === currentSlide.id) {
          nextSlide = sortedSlides[i + 1]
          isNextSlideLastSlide = i + 1 === sortedSlides.length - 1
        }
      }

      if (currentSlide.type === 'pickAnswer' && nextSlide && nextSlide.type === 'leaderboard') {
        // dont remove last leaderboard slide if there is quiz left in the presentation
        if (isNextSlideLastSlide && quizSlides.length !== deletedQuizSlides.length) {
          return
        }

        if (this.deletedSlides.map((i) => i.id).indexOf(nextSlide.id) !== -1) {
          return
        }

        this.deletedSlides.push(nextSlide)
      }
    },

    async handleCallAPIDeleteSlides() {
      // filter slide with no id because new slide dont have id
      const slidesWithId = this.presentation.slides.filter((slide) => slide.id)
      if (this.deletedSlides.length === slidesWithId.length) {
        this.hideModal()
        utils.showToastError('Cannot empty presentation. Please keep at least one slide.')
        return
      }

      try {
        this.setSyncLoadingAction(1)
        await SlideApi.updateSlides({
          presentationId: this.$route.params.id,
          slides: this.deletedSlides.map((s) => ({ id: s.id, deleted: true })),
        })

        this.updateLocalData()
        this.handleActiveSlide()
        this.setSyncLoadingAction(2)
        utils.showToastSuccess(`Delete ${this.deletedSlides.length} slide(s) successfully`)
      } catch (err) {
        this.setSyncLoadingAction(3)
        utils.showToastError(`Fail to delete ${this.deletedSlides.length} slide(s)`)
        throw err
      }
    },

    updateLocalData() {
      this.deletedSlides.forEach((s) => {
        const index = this.presentation.slides.findIndex((i) => s.id === i.id)
        this.presentation.slides.splice(index, 1)
      })
      PresentationUtils.checkAndUpdatePresentationHasResults()

      EventBus.$emit('updateLastSlide')
    },

    handleActiveSlide() {
      setTimeout(() => {
        this.$emit('deselectAllSlides')
        const activeIndex = this.presentation.slides.findIndex(
          (s) => s.id === this.slideActive.id,
        )
        if (activeIndex > -1) {
          this.$emit(
            'makeSlideActive',
            this.presentation.slides[
              activeIndex ? activeIndex - 1 : activeIndex
            ],
            true,
          )
        } else {
          const slideIndexArray = this.deletedSlides.map((s) => s.index)
          const minIndex = Math.min(...slideIndexArray) ?
            Math.min(...slideIndexArray) : slideIndexArray[0]
          this.$emit(
            'makeSlideActive',
            this.presentation.slides[minIndex ? minIndex - 1 : minIndex],
            true,
          )
        }
        this.updateSlideActiveAction({
          activeTab: null,
        })
        this.hideModal()
      })
    },

    hideModal() {
      this.$emit('hide')
    },
  },
}
</script>
